// The player notebook (issue 1166; decision D on #1062): the "Notebook" tool, every seated person's. Your own pages at
// this table — a list, a plain-text editor that saves itself a moment after you stop typing, and for a player a "Share
// with the DM" switch per page; Delete asks first. The DM's own notebook is theirs alone (no switch), and the DM has a
// second tab, "Shared with you": each player's shared pages, read-only, read on open, when the socket says they changed
// (`table:notebook`, which carries no page) and when the window comes back into focus. Text is drawn as text, never markup.
import { useCallback, useEffect, useRef, useState } from 'react';
import { NOTEBOOK_LIMITS, memberLabel, type NotebookPage, type NotebookPatch, type SharedNotebook } from '@dragons-table/vtt-shared';
import { useShell } from '../context';
import { useOptionalTableState } from '../../table/TableState';
import { ConfirmDialog } from '../Dialogs';
import { deletePage, myPages, newPage, onNotebookChanged, savePage, sharedWithMe } from '../../notebook';
import { BTN } from '../ui';

/** How long after the last keystroke a page saves itself. */
export const AUTOSAVE_MS = 700;
const DOWN = "The notebook can't be reached just now. Try again in a moment.";
const btn = `${BTN} shrink-0`;
const input = 'w-full rounded border border-stone-700 bg-stone-900 px-2 py-1 text-sm';

type Loaded<T> = { kind: 'loading' } | { kind: 'error'; reason: string } | { kind: 'ok'; value: T };

function PageEditor({ page, canShare, onChanged, onDelete }: {
  page: NotebookPage; canShare: boolean; onChanged: (p: NotebookPage) => void; onDelete: () => void;
}) {
  const { campaignId } = useShell();
  const [title, setTitle] = useState(page.title);
  const [body, setBody] = useState(page.body);
  const [said, setSaid] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ title: string; body: string } | null>(null);

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current; pending.current = null;
    if (!p) return;
    const patch: NotebookPatch = p.title.trim() ? { title: p.title, body: p.body } : { body: p.body };
    setSaid('Saving…');
    const got = await savePage(campaignId, page.id, patch);
    if (got.kind === 'ok') { onChanged(got.value); setSaid('Saved.'); } else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  }, [campaignId, page.id, onChanged]);
  // Whatever is still waiting is saved when the page is closed.
  useEffect(() => () => { void flush(); }, [flush]);

  const edit = (next: { title: string; body: string }) => {
    setTitle(next.title); setBody(next.body); setSaid('');
    pending.current = next;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, AUTOSAVE_MS);
  };
  const share = async (shared: boolean) => {
    const got = await savePage(campaignId, page.id, { shared });
    if (got.kind === 'ok') { onChanged(got.value); setSaid(shared ? 'Shared with the DM.' : 'No longer shared.'); } else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };

  return (
    <div className="space-y-2" data-testid="notebook-editor">
      <label className="block text-xs">Title
        <input className={input} value={title} maxLength={NOTEBOOK_LIMITS.title} onChange={(e) => edit({ title: e.target.value, body })} />
      </label>
      <label className="block text-xs">Page text
        <textarea className={`${input} h-48`} value={body} maxLength={NOTEBOOK_LIMITS.body} onChange={(e) => edit({ title, body: e.target.value })} />
      </label>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {canShare && (
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={page.shared} onChange={(e) => void share(e.target.checked)} />Share with the DM
          </label>
        )}
        <button type="button" className={btn} onClick={onDelete}>Delete page</button>
        <span className="text-stone-400" role="status">{said}</span>
      </div>
      {!canShare && <p className="text-xs text-stone-500">Your own notes: nobody else reads them.</p>}
    </div>
  );
}

function MyNotebook({ dm }: { dm: boolean }) {
  const { campaignId } = useShell();
  const [pages, setPages] = useState<Loaded<NotebookPage[]>>({ kind: 'loading' });
  const [open, setOpen] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<NotebookPage | null>(null);
  const [said, setSaid] = useState('');

  useEffect(() => {
    let live = true;
    void myPages(campaignId).then((got) => { if (live) setPages(got.kind === 'ok' ? got : { kind: 'error', reason: got.kind === 'refused' ? got.reason : DOWN }); });
    return () => { live = false; };
  }, [campaignId]);

  const changed = useCallback((p: NotebookPage) => setPages((was) => (was.kind === 'ok' ? { kind: 'ok', value: was.value.map((x) => (x.id === p.id ? p : x)) } : was)), []);
  const write = async () => {
    const got = await newPage(campaignId, 'New page');
    if (got.kind === 'ok') { setPages((was) => (was.kind === 'ok' ? { kind: 'ok', value: [...was.value, got.value] } : was)); setOpen(got.value.id); setSaid(''); } else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };
  const remove = async (p: NotebookPage) => {
    setDeleting(null);
    const got = await deletePage(campaignId, p.id);
    if (got.kind === 'ok') { setPages((was) => (was.kind === 'ok' ? { kind: 'ok', value: was.value.filter((x) => x.id !== p.id) } : was)); setOpen(null); setSaid(`Deleted "${p.title}".`); } else setSaid(got.kind === 'refused' ? got.reason : DOWN);
  };

  if (pages.kind === 'loading') return <p className="text-stone-400">Reading your notebook…</p>;
  if (pages.kind === 'error') return <p className="text-stone-400">{pages.reason}</p>;
  const current = pages.value.find((p) => p.id === open) ?? null;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" className={btn} onClick={() => void write()}>New page</button>
        {said && <span className="text-xs text-stone-400">{said}</span>}
      </div>
      {pages.value.length === 0 && <p className="text-stone-400">{dm ? 'No pages yet. Your notes here are yours alone.' : 'No pages yet. Your notes are yours alone until you share a page with the DM.'}</p>}
      <ul className="flex flex-wrap gap-1" aria-label="Your pages">
        {pages.value.map((p) => (
          <li key={p.id}>
            <button type="button" aria-current={p.id === open ? 'page' : undefined}
              className={`rounded px-2 py-0.5 text-xs ${p.id === open ? 'bg-stone-700 text-stone-100' : 'text-stone-300 hover:bg-stone-800'}`} onClick={() => setOpen(p.id)}>{p.title}</button>
            {p.shared && <span className="ml-0.5 text-[0.625rem] text-amber-400" title="Shared with the DM">shared</span>}
          </li>
        ))}
      </ul>
      {current && <PageEditor key={current.id} page={current} canShare={!dm} onChanged={changed} onDelete={() => setDeleting(current)} />}
      {deleting && (
        <ConfirmDialog title={`Delete "${deleting.title}"?`} body="The page and its text are gone for good." confirmLabel="Delete page" destructive
          onConfirm={() => void remove(deleting)} onClose={() => setDeleting(null)} />
      )}
    </div>
  );
}

function SharedWithYou() {
  const { campaignId, table } = useShell();
  const tableState = useOptionalTableState();
  const [list, setList] = useState<Loaded<SharedNotebook[]>>({ kind: 'loading' });
  const load = useCallback(async () => {
    const got = await sharedWithMe(campaignId);
    setList(got.kind === 'ok' ? got : { kind: 'error', reason: got.kind === 'refused' ? got.reason : DOWN });
  }, [campaignId]);
  useEffect(() => {
    void load();
    const stop = onNotebookChanged((p) => { if (p === campaignId) void load(); });
    const onFocus = () => { void load(); };
    window.addEventListener('focus', onFocus);
    return () => { stop(); window.removeEventListener('focus', onFocus); };
  }, [load, campaignId]);

  if (list.kind === 'loading') return <p className="text-stone-400">Reading what players shared…</p>;
  if (list.kind === 'error') return <p className="text-stone-400">{list.reason}</p>;
  if (list.value.length === 0) return <p className="text-stone-400">No player has shared a page with you yet.</p>;
  const nameOf = (n: SharedNotebook) => {
    const m = table.members.find((x) => x.id === n.author);
    return m ? memberLabel(m, (tableState?.store.state.objects ?? {}) as never) : n.handle ? `@${n.handle}` : n.displayName ?? 'a player';
  };
  return (
    <div className="space-y-3 overflow-y-auto" data-testid="notebook-shared">
      {list.value.map((n) => (
        <section key={n.author} aria-label={nameOf(n)} className="space-y-2">
          <h3 className="vtt-section-head">{nameOf(n)}</h3>
          {n.pages.map((p) => (
            <article key={p.id} className="rounded border border-stone-800 p-2" data-testid="notebook-shared-page">
              <h4 className="font-semibold">{p.title}</h4>
              <p className="whitespace-pre-wrap break-words text-stone-300">{p.body}</p>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}

export function NotebookPanel() {
  const { table } = useShell();
  const dm = table.me.role === 'dm';
  const [tab, setTab] = useState<'mine' | 'shared'>('mine');
  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-2 text-sm" data-testid="notebook">
      {dm && (
        <div className="flex gap-1" role="tablist" aria-label="Notebook">
          {([['mine', 'My notebook'], ['shared', 'Shared with you']] as const).map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id}
              className={`rounded px-2 py-0.5 text-xs ${tab === id ? 'bg-stone-700 text-stone-100' : 'text-stone-400 hover:bg-stone-800'}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      )}
      {dm && tab === 'shared' ? <SharedWithYou /> : <MyNotebook dm={dm} />}
    </div>
  );
}
